'use client';

import type { SportMatchModel } from '@nf/types/odds';
import type { LiveTimer } from '@nf/types/live-timer';
import type { OddsPageLayout } from '@/types';
import { sportsWithPeriodScores } from '@/settings/sports-settings';
import { periodScoresService } from '../period-scoreboard/services/period-scores.service';
import { useLiveTimer } from '../hooks/use-live-timer';
import { Breakpoint } from '@/features/breakpoint';
import { useBreakpoint } from '@/features/breakpoint/contexts';

import { MatchEventCard } from './components/match-event-card';
import { PeriodScoreboard } from '../period-scoreboard';

interface MatchInfoProps {
	match: SportMatchModel;
	betTypeCount: number;
	oddsPageLayout: OddsPageLayout;
}

export const MatchInfo = ({
	match,
	betTypeCount,
	oddsPageLayout
}: MatchInfoProps) => {
	const liveTimer: LiveTimer = useLiveTimer(match);
	const { breakpoint } = useBreakpoint();
	const periodScores = periodScoresService.getPeriodScores(match);
	const isCardLayout = oddsPageLayout === 'card';
	
	// period scoreboard display logic:
	// - Card View: 
	//   ○ if event status is present and breakpoint > 1024, 
	//   ○ or if event status is not provided
	// - List View: Always show.
	let shouldShowPeriodScoreboard = sportsWithPeriodScores.includes(match.GameID);
	if (isCardLayout) {
		shouldShowPeriodScoreboard = liveTimer.event 
			? breakpoint > Breakpoint.md 
			: true;
	}

	const [matchEventCard, periodScoreboard] = [
		<MatchEventCard 
			key="match-event-card"
			match={match}
			liveTimer={liveTimer}
			betTypeCount={betTypeCount}
			oddsPageLayout={oddsPageLayout}
		/>,
		shouldShowPeriodScoreboard ? (
			<PeriodScoreboard 
				key="period-scoreboard"
				scores={periodScores}
			/>
		) : null
	];

	return isCardLayout ? (
		<>
			{matchEventCard}
			{periodScoreboard}
		</>
	) : (
		<>
			{periodScoreboard}
			{matchEventCard}
		</>
	);
};